import { z } from 'zod/v4';
import { DROP_BOX_FILE_TYPES, type DropBoxFileType } from '../../constants.js';

/**
 * The drop-box contract: `POST /api/files/drop-box/classify` queues a fast
 * classification of up to 10 uploaded files, `…/classify/results` reads each
 * file's latest one back, and `…/accept` files them as the type the user
 * accepts.
 */

const fileIdsArray = z
  .array(z.string().min(1))
  .min(1, 'At least one file ID is required')
  .max(10, 'Maximum 10 files can be processed at once');

export const DropBoxClassifyRequestSchema = z.object({
  fileIds: fileIdsArray,
});

export type IDropBoxClassifyRequest = z.infer<
  typeof DropBoxClassifyRequestSchema
>;

export const DropBoxClassifyResponseItemSchema = z.object({
  fileId: z.string(),
  aiProcessingJobId: z.string(),
});

export const DropBoxClassifyResponseSchema = z.object({
  enqueued: z.array(DropBoxClassifyResponseItemSchema),
  failed: z.array(z.object({ fileId: z.string(), reason: z.string() })),
});

export const DropBoxClassificationStatusSchema = z.enum([
  'pending',
  'processing',
  'completed',
  'failed',
]);

const DROP_BOX_FILE_TYPE_VALUES = Object.values(DROP_BOX_FILE_TYPES) as [
  DropBoxFileType,
  ...DropBoxFileType[],
];

export const DropBoxFileTypeSchema = z.enum(DROP_BOX_FILE_TYPE_VALUES);

export const DropBoxClassifyResultsRequestSchema = z.object({
  fileIds: fileIdsArray,
});

export const DropBoxClassifyResultItemSchema = z.object({
  fileId: z.string(),
  aiProcessingJobId: z.string().nullable(),
  status: DropBoxClassificationStatusSchema,
  classification: z
    .object({
      type: DropBoxFileTypeSchema,
      confidence: z.number(),
      reasoning: z.string(),
    })
    .nullable(),
  error: z.string().nullable(),
});

export const DropBoxClassifyResultsResponseSchema = z.object({
  results: z.array(DropBoxClassifyResultItemSchema),
});

export type IDropBoxClassifyResultItem = z.infer<
  typeof DropBoxClassifyResultItemSchema
>;

export const DropBoxAcceptFileItemSchema = z.object({
  fileId: z.string().min(1),
  acceptedType: DropBoxFileTypeSchema,
});

export const DropBoxAcceptRequestSchema = z.object({
  files: z
    .array(DropBoxAcceptFileItemSchema)
    .min(1, 'At least one file is required')
    .max(10, 'Maximum 10 files can be accepted at once'),
});

export type IDropBoxAcceptRequest = z.infer<typeof DropBoxAcceptRequestSchema>;

export const DropBoxAcceptResponseItemSchema = z.object({
  fileId: z.string(),
  acceptedType: DropBoxFileTypeSchema,
  workflowId: z.string().nullable(),
});

export const DropBoxAcceptSkippedItemSchema = z.object({
  fileId: z.string(),
  reason: z.string(),
});

export const DropBoxAcceptResponseSchema = z.object({
  accepted: z.array(DropBoxAcceptResponseItemSchema),
  skipped: z.array(DropBoxAcceptSkippedItemSchema),
});
